import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService, PhotographyApiService } from './api.service';
import { PhotographyPhotographer } from '../models/photography-catalog.model';
import { PhotographyListResponse } from '../models/photography-vendor.model';

/** Mirrors CreatePhotographerRequest in @lcms-photography/core. Updates send any subset of these. */
export interface PhotographyPhotographerRequest {
  name?: string;
  /** The API lowercases it */
  email?: string;
  /** Free text, stored as given. null clears it (update only). */
  phone?: string | null;
  firstName?: string;
  lastName?: string;
}

/**
 * Photographers in the lcms-photography catalog. One can work for several vendors: list a vendor's photographers and
 * link or unlink them with PhotographyVendorService.
 */
@Injectable({
  providedIn: 'root',
})
export class PhotographyPhotographerService {
  private api = inject(PhotographyApiService);
  private managementApi = inject(ApiService);

  readonly resource = 'photographers';

  get$(photographerId: string): Observable<PhotographyPhotographer> {
    return this.api.get<PhotographyPhotographer>(`${this.resource}/${photographerId}`);
  }

  /** The photographer with this email, ignoring case, if there is one */
  findByEmail$(email: string): Observable<PhotographyPhotographer | undefined> {
    return this.api.get<PhotographyListResponse<PhotographyPhotographer>>(this.resource, { email, limit: 1 }).pipe(
      map((response) => response.items[0]),
    );
  }

  /** Rejects with a 409 when the email is taken */
  create(photographer: PhotographyPhotographerRequest): Promise<PhotographyPhotographer> {
    return this.managementApi.create<PhotographyPhotographer, PhotographyPhotographerRequest>(`photography/${this.resource}`, photographer);
  }

  update(photographerId: string, photographer: PhotographyPhotographerRequest): Promise<PhotographyPhotographer> {
    const route = `${this.resource}/${photographerId}`;
    return this.managementApi.update<PhotographyPhotographer, PhotographyPhotographerRequest>(`photography/${route}`, photographer);
  }

  /** Rejects with a 409 while the photographer is still linked to a vendor */
  delete(photographerId: string): Promise<void> {
    return firstValueFrom(this.managementApi.delete<void>(`photography/${this.resource}/${photographerId}`));
  }
}
